// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The four-step onboarding strip (G2-11; 08 §3, and the conventions' "onboarding progress bar").
//
// > *Play a ball → make a ramp → start a mode → save your score.*
//
// The decisions are in `onboarding.ts` and this draws them. What is here is the two things a
// component owns: where it sits, and what it does not cover.
//
// **Under the machine, never over it.** 08 §1 gives the first thirty seconds to the machine, and a
// strip laid across a playfield is a tutorial competing with the thing it is teaching. It also does
// not appear at all until the first step is done — see `showOnboarding` — so its first appearance
// is a step lit rather than a list of homework.
//
// **The last step is the only one that is an offer.** Three of the four describe things that have
// happened; "save your score" is where an anonymous player finds out a score can be kept, which is
// why it carries a link and the others do not. A strip with a link on every row would be four
// upsells wearing a progress bar.

import { Link } from 'react-router'
import { useLocalisation } from '../i18n/useLanguage'
import type { MessageKey } from '../i18n'
import {
  ONBOARDING_STEPS,
  nextOnboardingStep,
  onboardingFraction,
  showOnboarding,
  type OnboardingProgress,
  type OnboardingStep,
} from '../onboarding'

export interface OnboardingStripProps {
  readonly progress: OnboardingProgress
}

/** What each step is called. `MessageKey`, so a step added without copy does not compile. */
const LABELS: Readonly<Record<OnboardingStep, MessageKey>> = {
  playBall: 'onboarding.play-ball',
  makeRamp: 'onboarding.make-ramp',
  startMode: 'onboarding.start-mode',
  saveScore: 'onboarding.save-score',
}

export function OnboardingStrip({ progress }: OnboardingStripProps): React.JSX.Element | null {
  const { t } = useLocalisation()
  if (!showOnboarding(progress)) return null
  const next = nextOnboardingStep(progress)
  const done = progress.size

  return (
    <section
      className="onboarding"
      // The goal gradient, named once for assistive technology rather than left to be counted off
      // the list. A progress bar a screen reader has to add up is a progress bar it cannot report.
      //
      // The label sits immediately after the role because that is where `AccessibilityContracts`
      // looks — it reads the characters following `role="group"` — and a named group whose name is
      // three attributes away is a group the guard reports as unnamed. Which is the right rule to
      // keep: a scanner that searched the whole element would also pass a name belonging to
      // something else.
      role="group"
      aria-label={t('onboarding.title')}
    >
      <p className="onboarding__lede">
        {t('onboarding.progress', { done, total: ONBOARDING_STEPS.length })}
      </p>
      <ol className="onboarding__steps">
        {ONBOARDING_STEPS.map((step) => {
          const complete = progress.has(step)
          return (
            <li
              key={step}
              className={
                complete
                  ? 'onboarding__step onboarding__step--done'
                  : step === next
                    ? 'onboarding__step onboarding__step--next'
                    : 'onboarding__step'
              }
            >
              {/*
                A tick or a number, and never a colour alone: 08 §3's own rule about the inserts
                applies to a checklist too. `aria-hidden` because the list item's text already says
                which step it is and the state is on the item.
              */}
              <span className="onboarding__mark" aria-hidden="true">
                {complete ? '✓' : String(ONBOARDING_STEPS.indexOf(step) + 1)}
              </span>
              <span className="onboarding__label">{t(LABELS[step])}</span>
              <span className="sr-only">
                {complete ? t('onboarding.done') : step === next ? t('onboarding.next') : ''}
              </span>
            </li>
          )
        })}
      </ol>
      {/*
        The one link, on the one step that is an offer. `?want=cloud-scores` is the capability the
        rest of the product names for exactly this — the same convention the arcade's locked tiles
        and `EnterHeadset` use — so the account page resolves the entitlement rather than this strip
        deciding what is being sold.
      */}
      {next === 'saveScore' ? (
        <Link className="button button--primary" to="/account?want=cloud-scores" title={t('onboarding.save-title')}>
          {t('onboarding.save-cta')}
        </Link>
      ) : null}
      {/*
        The bar itself, drawn from the same number the sentence reports so the two cannot disagree.
        `aria-hidden` because the sentence above is the accessible version of it: a progress bar and
        a sentence saying the same thing is one announcement too many.
      */}
      <div
        className="onboarding__bar"
        aria-hidden="true"
        style={{ ['--onboarding-progress' as string]: onboardingFraction(progress).toFixed(3) }}
      />
    </section>
  )
}
